import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation
} from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';

import { BehaviorSubject, combineLatest, NEVER, Observable } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { LanguageCode } from '@language';
import {
    ActionItem,
    BaseContainer,
    ComplexMapFieldValue,
    FormSelectOption,
    LoadingBehaviorSubject,
    MultiFormSelectOptions,
    multiFormSelectOptionWrapper,
    PaginatedResults,
    PaginationInfo,
    toFormSelectOption
} from '@stylehaven/components';
import { GenericQueryPart, Operator, Strategy } from '../../entities/generic-query-part.entity';
import { SearchableField, WebserviceConfig } from '../../entities/webservice-config.entity';
import { ExternalWebservice } from '../../services/external-webservice.service';

@Component({
    selector: 'med-webservice-advanced-search-container',
    templateUrl: './webservice-advanced-search-container.component.html',
    styleUrls: ['./webservice-advanced-search-container.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ExternalWebservice],
    encapsulation: ViewEncapsulation.None
})
export class WebserviceAdvancedSearchContainerComponent extends BaseContainer implements OnInit, OnDestroy {
    public lookupLoading$: LoadingBehaviorSubject = new LoadingBehaviorSubject();
    public results$: Observable<PaginatedResults<ComplexMapFieldValue>>;
    public searchActionItem$: Observable<ActionItem>;

    public formArray: UntypedFormArray;

    public entryControls$: BehaviorSubject<UntypedFormControl[][]> = new BehaviorSubject<UntypedFormControl[][]>([]);

    public fieldOptions: MultiFormSelectOptions;
    public strategyOptions: MultiFormSelectOptions;
    public operatorOptions: MultiFormSelectOptions;

    private query$: Observable<GenericQueryPart[]>;
    public searchTrigger$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    private paginationInfo$: BehaviorSubject<PaginationInfo> = new BehaviorSubject<PaginationInfo>({
        page: 0,
        resultsPerPage: 5
    });

    @Input() webserviceConfig: WebserviceConfig;
    @Input() multiple: boolean;

    @Output() selected: EventEmitter<ComplexMapFieldValue[]> = new EventEmitter<ComplexMapFieldValue[]>();

    constructor(
        private webservice: ExternalWebservice,
        private readonly formBuilder: UntypedFormBuilder
    ) {
        super();
    }

    ngOnInit(): void {
        if (!this.webserviceConfig) {
            console.error('WebserviceAdvancedSearchComponent:: No webservice config provided');
            return;
        }

        this.initWebservice();

        this.initSearchFieldOptions();
        this.initSearchStrategyOptions();
        this.initSearchOperatorOptions();

        this.initRowFormArray();

        this.initQuery();

        this.initSearchActionItem();
        this.initWebserviceSearchResults();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();

        this.loading$.complete();
        this.lookupLoading$.complete();
        this.searchTrigger$.complete();
        this.entryControls$.complete();
        this.paginationInfo$.complete();
    }

    private initWebservice(): void {
        this.webservice.initWebservice(this.webserviceConfig);
    }

    // #region Init options
    private initSearchFieldOptions(): void {
        this.fieldOptions = multiFormSelectOptionWrapper(
            this.getSearchFieldFormSelectOptions(this.webserviceConfig.searchableFields)
        );
    }

    private getSearchFieldFormSelectOptions(searchableFields: SearchableField[] = []): FormSelectOption[] {
        const currentLang: LanguageCode = this.translateService.getCurrentLanguage();
        const options: FormSelectOption[] = searchableFields.map(field => {
            const label: string | undefined = field.Translations?.find(
                translation => currentLang.toLowerCase() === translation.Lang.toLowerCase()
            )?.Value;

            return toFormSelectOption(field.Key, label);
        });

        return options;
    }

    private initSearchStrategyOptions(): void {
        this.strategyOptions = this.enumToMultiFormSelectOptions(Strategy, 'advanced-search.strategy');
    }

    private initSearchOperatorOptions(): void {
        this.operatorOptions = this.enumToMultiFormSelectOptions(Operator, 'advanced-search.operator');
    }

    private enumToMultiFormSelectOptions<T extends { [key: string]: string }>(
        enumType: T,
        languageKey: string
    ): MultiFormSelectOptions<string> {
        const options: FormSelectOption<string>[] = Object.entries(enumType).map(([key, value]) =>
            toFormSelectOption(value, this.translateService.translate(`${languageKey}.${value}`))
        );

        return multiFormSelectOptionWrapper(options);
    }
    // #endregion

    // #region Init search
    private initQuery(): void {
        this.query$ = this.searchTrigger$.pipe(map(() => this.formArrayToQuery(this.formArray.value)));
    }

    private formArrayToQuery(formArrayValues: Record<string, string[] | string>[]): GenericQueryPart[] {
        const queryParts: GenericQueryPart[] = formArrayValues
            .map(rowValues => this.rowValuesToQueryPart(rowValues))
            .filter((queryPart): queryPart is GenericQueryPart => !!queryPart);

        return queryParts ?? [];
    }

    private rowValuesToQueryPart(rowValues: Record<string, any>): GenericQueryPart | null {
        const strategy: Strategy | null = rowValues.Strategy ? rowValues.Strategy[0] : null;
        const operator: Operator | null = rowValues.Operator ? rowValues.Operator[0] : null;
        const field: string | null = rowValues.Field ? rowValues.Field[0] : null;
        const value: string | null = rowValues.Value ? rowValues.Value : null;

        if (!strategy || !operator || !field || !value) {
            return null;
        }

        return {
            strategy,
            operator,
            field,
            value
        };
    }

    private initSearchActionItem(): void {
        this.searchActionItem$ = this.loading$.pipe(map((isLoading: boolean) => this.getSearchActionItem(isLoading)));
    }

    private getSearchActionItem(isLoading: boolean): ActionItem {
        return {
            label: this.translateService.translate('actions.search'),
            icon: 'search',
            eventListener: this.onSearch.bind(this),
            disabled: isLoading
        };
    }

    private initWebserviceSearchResults(): void {
        this.results$ = combineLatest([this.query$, this.paginationInfo$]).pipe(
            tap(() => this.lookupLoading$.startLoading()),
            switchMap(([query, pagination]) => this.webservice.search(query, pagination)),
            tap(() => this.lookupLoading$.stopLoading()),
            catchError(() => {
                this.lookupLoading$.stopLoading();
                return NEVER;
            })
        );
    }
    // #endregion

    // #region Init rows and controls
    private initRowFormArray(): void {
        this.formArray = new UntypedFormArray([]);
        this.addRow(0);
    }

    public addRow(newRowIndex: number): void {
        this.formArray.insert(newRowIndex, this.createNewRow());
    }

    public removeRow(rowIndex: number): void {
        this.formArray.removeAt(rowIndex);
    }

    private createNewRow(): UntypedFormGroup {
        return this.formBuilder.group({
            Strategy: this.formBuilder.control([Strategy.AND]),
            Field: this.formBuilder.control(null),
            Operator: this.formBuilder.control([Operator.EQUALS]),
            Value: this.formBuilder.control(null)
        });
    }

    public onSearch(): void {
        this.searchTrigger$.next(true);
    }

    public onPaginationChange(pagination: PaginationInfo): void {
        this.paginationInfo$.next(pagination);
    }

    public onSelection(selection: ComplexMapFieldValue[]): void {
        this.selected.next(selection);
    }
}
